import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { Button } from '@ds/components/core/Button';

const CHECK_EVERY_MS = 60 * 60 * 1000;

interface PwaUpdateState {
  needRefresh: boolean;
  updating: boolean;
  update: () => void;
}

const PwaUpdateContext = createContext<PwaUpdateState>({ needRefresh: false, updating: false, update: () => {} });

export function usePwaUpdate(): PwaUpdateState {
  return useContext(PwaUpdateContext);
}

/** Registra o service worker UMA vez (0018.1) e avisa quando há versão nova esperando.
 * Checa atualização ao voltar pro app (no iOS ele fica suspenso em segundo plano e nada
 * dispara sozinho), ao voltar a internet e de hora em hora. */
export function PwaUpdateProvider({ children }: { children: ReactNode }) {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updating, setUpdating] = useState(false);
  const updateSW = useRef<(reloadPage?: boolean) => Promise<void>>(async () => {});

  useEffect(() => {
    let registration: ServiceWorkerRegistration | undefined;
    const check = () => {
      void registration?.update().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    updateSW.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setNeedRefresh(true),
      onRegisteredSW: (_swUrl, r) => {
        registration = r;
      },
    });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', check);
    const interval = window.setInterval(check, CHECK_EVERY_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', check);
      window.clearInterval(interval);
    };
  }, []);

  const update = useCallback(() => {
    setUpdating(true);
    void updateSW.current(true);
  }, []);

  const value = useMemo(() => ({ needRefresh, updating, update }), [needRefresh, updating, update]);
  return <PwaUpdateContext.Provider value={value}>{children}</PwaUpdateContext.Provider>;
}

/** Faixa no fluxo (não `fixed`: já houve elemento fixo cobrindo a barra de abas, 0021.1). */
export function PwaUpdateBanner() {
  const { needRefresh, updating, update } = usePwaUpdate();
  if (!needRefresh) return null;
  return (
    <div
      role="status"
      data-testid="pwa-update-banner"
      style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', padding: '8px var(--gutter-page)', background: 'var(--brand-600)', color: '#fff', font: 'var(--type-label)' }}
    >
      <span>Nova versão disponível.</span>
      <Button size="sm" variant="secondary" loading={updating} data-testid="pwa-update-button" onClick={update}>
        Atualizar
      </Button>
    </div>
  );
}
